import { Component, Input, ViewChild } from '@angular/core';
import { EditableContentComponent, EditableContentDisplayComponent, EditableContentEditComponent, SingleFocusService } from 'components/input/editable-content/editable-content.component';
import { EditableContentDirective } from 'components/input/editable-content/editable-content.directive';
import { CompanyModel } from 'models/generated/Entities.GormCrm.Company';
import { CompanyContactModel } from 'models/generated/Entities.GormCrm.CompanyContact';
import { ContactModel } from 'models/generated/Entities.GormCrm.Contact';
import { ContactPhoneModel } from 'models/generated/Entities.GormCrm.ContactPhone';
import { Layout } from 'modules/layout';
import { TranslatePipe } from 'pipes/translate.pipe';
import { CompanyService } from 'services/company.service';
import { FormValidationService } from 'services/form-validation.service';
import { ContactComponent } from './contact.component';

@Component({
	selector: 'list-contact',
	templateUrl: './list-contact.component.html',
	providers: [FormValidationService, SingleFocusService],
	imports: [Layout, EditableContentDirective, EditableContentComponent, EditableContentDisplayComponent, EditableContentEditComponent, ContactComponent, TranslatePipe]
})
export class ListContactComponent {

	contacts: ContactModel[] = [];
	newContact: ContactModel;

	@Input() showJustDefaultContactForBilling = false;
	@Input() enableEdit = true;
	@Input() enableDelete = true;

	billingContactId?: string;
	primaryContactId?: string;

	private _company: CompanyModel = new CompanyModel();
	@Input()
	set company(company: CompanyModel) {
		this._company = company;
		this.setCompany(company);
	}
	get company() {
		return this._company;
	}
	@ViewChild('editNew') newContactEditor?: EditableContentComponent;

	constructor(private companyService: CompanyService) {
		this.newContact = this.companyService.getNewContact();
	}

	contactSymbols(contact: ContactModel): string | null {
		if (contact.id && contact.id === this.company.primaryContactId) return '1';
		if (contact.id && contact.id === this.company.billingContactId) return '$';
		return null;
	}

	setCompany(company: CompanyModel | undefined) {
		if (!company) return;
		
		this.billingContactId = company.billingContactId;
		this.primaryContactId = company.primaryContactId!;
		if (this.company.companyContacts.length > 0) {
			this.company.companyContacts.sort((a, b) => {
				return a.contact!.id === this.company.primaryContactId ? -1 : 1;
			});

			this.company.companyContacts.forEach((companyContact: CompanyContactModel) => {
				for (let i = companyContact.contact!.contactPhones.length; i < 2; i++) {
					let contactPhone = new ContactPhoneModel();
					contactPhone.priority = i;
					contactPhone.type = i;
					companyContact.contact!.contactPhones.push(contactPhone);
				}
				companyContact.contact!.contactPhones.sort((a, b) => {
					return a.priority - b.priority;
				});
			});
		} else if (!this.showJustDefaultContactForBilling) {
			let contact = this.companyService.getNewContact();
			let companyContact = new CompanyContactModel();
			companyContact.company = this.company;
			companyContact.contact = contact;
			this.company.companyContacts.push(companyContact);
		}
	}

	companyContacts(): ContactModel[] {
		if (!this.company) return [];
		if (this.showJustDefaultContactForBilling) 
			return this.company.billingContact ? [this.company.billingContact!]	: [this.company.primaryContact!];
		return this.company.companyContacts.map(cc => cc.contact!);
	}

	updateContact(contactId: string, contact: ContactModel) {
		contact.fullName = contact.firstName + ' ' + contact.lastName;
		const index = this.company.companyContacts.findIndex(cc => cc.contact!.id == contactId);
		if (index < 0) return;

		this.company.primaryContactId = this.primaryContactId;
		this.company.companyContacts[index].contact = contact;
		this.companyService.updateContact(contact, this.company.id!, contact.isPrimary ? true : false);
	}

	createContact(contact: ContactModel) {
		contact.fullName = contact.firstName + ' ' + contact.lastName;
		let companyContact = new CompanyContactModel();
		this.company.primaryContactId = this.primaryContactId;
		companyContact.company = this.company;
		companyContact.contact = contact;
		contact.companyContacts.push(companyContact);
		this.company!.companyContacts.push(companyContact);
		this.companyService.updateContact(contact, this.company.id!, contact.isPrimary ? true : false);
	}

	deleteContact(contact: ContactModel) {
		const index = this.company.companyContacts.findIndex(cc => cc.contact!.id == contact.id);
		if (index < 0) return;

		this.company.companyContacts.splice(index, 1);
		this.companyService.deleteContact(contact, this.company.id!);
	}

	contactIsValid(contact: ContactModel): boolean {
		// Invalid contacts are allowed to exist, but will be marked as invalid using this validation function, necessary for new sales where all information might not be available yet before the sale gets confirmed
		const isPrimary = contact.id === this.company.primaryContactId;
		return this.companyService.isContactValid(contact, isPrimary);
	}

	editNewContact() {
		this.newContactEditor?.beginEdit();
	}
}
